import { useMemo, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { WrapFeest } from './WrapFeest';

// Ruim genoeg voor brede zoektermen als "thee" of "kaas", die inmiddels tegen
// de 20-30 treffers in de AH-catalogus kunnen hebben. De lijst scrollt.
const MAX_SUGGESTIES = 30;

export interface ProductFormWaarden {
  naam: string;
  aantal: number;
  eenheid: string;
}

interface Props {
  /** Levert `false` als de regel niet is toegevoegd, zodat het formulier blijft
   *  staan — bijvoorbeeld wanneer de duplicaatvraag nog beantwoord moet worden. */
  onSubmit: (waarden: ProductFormWaarden) => Promise<boolean>;
  bezig: boolean;
  knopLabel: string;
  idPrefix: string;
  /** Eerder gebruikte namen voor de autocomplete; leeg = geen suggesties. */
  suggesties?: { naam: string; eenheid: string; zoektermen?: string[] }[];
}

const LEEG = { naam: '', aantal: '1', eenheid: '' };

/** Leestekens (apostrof, koppelteken, &, +, …) tellen niet mee bij het zoeken
 *  — "kip kerrie" moet ook "Kip-kerrie" vinden en omgekeerd. */
const normaliseer = (tekst: string) =>
  tekst
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Formulier voor zowel de boodschappenlijst als de voorraad — dezelfde velden,
 *  dezelfde validatie. Stond in de oude app twee keer, met net andere regels. */
export function ProductForm({ onSubmit, bezig, knopLabel, idPrefix, suggesties = [] }: Props) {
  const [waarden, setWaarden] = useState(LEEG);
  const [fout, setFout] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [gemarkeerd, setGemarkeerd] = useState(0);
  const [wrapFeest, setWrapFeest] = useState(0);

  /** Elke optie heeft een eigen id nodig: `aria-activedescendant` op het
   *  invoerveld verwijst ernaar, en dat is hoe een schermlezer hoort wélke
   *  suggestie gemarkeerd staat terwijl je er met de pijltjes langs gaat. */
  const optieId = (i: number) => `${idPrefix}-suggestie-${i}`;

  /** Pas filteren zodra er iets getypt is: anders staat bij focus meteen de
   *  hele AH-catalogus (inmiddels honderd-plus namen) open. */
  const gefilterd = useMemo(() => {
    const zoekterm = normaliseer(waarden.naam);
    if (!zoekterm) return [];
    // Ook op `zoektermen` zoeken, niet alleen op de naam: een productnaam noemt
    // lang niet altijd wat het ís. "AH Les pains boulogne heel" is brood, en wie
    // "brood" typt hoort hem te zien. Die woorden staan bewust naast de naam en
    // niet erin, zodat de lijst leesbaar blijft.
    return suggesties
      .filter((s) =>
        [s.naam, ...(s.zoektermen ?? [])].some((tekst) => normaliseer(tekst).includes(zoekterm)),
      )
      .slice(0, MAX_SUGGESTIES);
  }, [waarden.naam, suggesties]);

  const zet = (veld: keyof typeof LEEG, waarde: string) => {
    setWaarden((w) => ({ ...w, [veld]: waarde }));
    if (fout) setFout(null);
  };

  /** Kiest iemand een suggestie, dan vult de eenheid zich mee — die is bij
   *  hetzelfde product bijna altijd dezelfde, en het scheelt een veld typen.
   *  Verandert de naam naar een ander product, dan verandert de eenheid mee;
   *  wordt het veld leeggemaakt, dan gaat de eenheid ook weer leeg — anders
   *  blijft die van het vorige product staan terwijl er niets meer gekozen is. */
  const kiesNaam = (waarde: string) => {
    const suggestie = suggesties.find((s) => s.naam === waarde);
    setWaarden((w) => ({
      ...w,
      naam: waarde,
      eenheid: waarde.trim() === '' ? '' : suggestie ? suggestie.eenheid : w.eenheid,
    }));
    if (fout) setFout(null);
  };

  const typNaam = (waarde: string) => {
    kiesNaam(waarde);
    setOpen(true);
    setGemarkeerd(0);
  };

  const kiesSuggestie = (naam: string) => {
    kiesNaam(naam);
    setOpen(false);
    if (naam.toLowerCase().includes('wraps')) setWrapFeest((n) => n + 1);
  };

  /** Toetsenbesturing die een <datalist> anders gratis zou geven: pijltjes om
   *  te markeren, Enter om te kiezen, Escape om te sluiten. */
  const toetsInNaamveld = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || gefilterd.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setGemarkeerd((i) => (i + 1) % gefilterd.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setGemarkeerd((i) => (i - 1 + gefilterd.length) % gefilterd.length);
    } else if (e.key === 'Enter') {
      const gekozen = gefilterd[gemarkeerd];
      if (gekozen) {
        e.preventDefault();
        kiesSuggestie(gekozen.naam);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const naam = waarden.naam.trim();
    if (!naam) return setFout('Vul een productnaam in');

    const aantal = Number(waarden.aantal);
    if (!Number.isFinite(aantal) || aantal <= 0) return setFout('Vul een geldig aantal in');

    if (await onSubmit({ naam, aantal, eenheid: waarden.eenheid.trim() })) {
      setWaarden(LEEG);
    }
  }

  return (
    <form className="vs-form" onSubmit={submit} noValidate>
      <WrapFeest trigger={wrapFeest} />
      <div className="vs-field vs-form__field--naam">
        <label className="vs-label" htmlFor={`${idPrefix}-naam`}>
          Product
        </label>
        <div className="vs-autocomplete">
          <input
            id={`${idPrefix}-naam`}
            className="vs-input"
            value={waarden.naam}
            onChange={(e) => typNaam(e.target.value)}
            onKeyDown={toetsInNaamveld}
            onFocus={() => waarden.naam && setOpen(true)}
            onBlur={() => setOpen(false)}
            placeholder="bijv. melk, koffie, bier…"
            autoComplete="off"
            // Wachtwoordmanagers (1Password, LastPass, Bitwarden, Dashlane) kijken
            // niet alleen naar `autocomplete`, maar bieden ook op simpele
            // heuristiek (eerste tekstveld in een formulier) aan om hier een
            // opgeslagen wachtwoord in te vullen. Deze data-attributen zijn hun
            // eigen, officieel ondersteunde manier om een veld over te slaan.
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            role="combobox"
            aria-expanded={open && gefilterd.length > 0}
            aria-controls={`${idPrefix}-suggesties`}
            aria-activedescendant={
              open && gefilterd.length > 0 ? optieId(gemarkeerd) : undefined
            }
            aria-autocomplete="list"
            aria-invalid={fout ? true : undefined}
          />
          {/* Eigen dropdown in plaats van een <datalist>: die laat de browser
              zelf positioneren (soms naast het veld) en is niet te stylen naar
              de huisstijl. Dit valt altijd onder het veld en oogt als de rest
              van de kaarten/dialogen in de app. */}
          {open && gefilterd.length > 0 && (
            <ul className="vs-autocomplete__lijst" id={`${idPrefix}-suggesties`} role="listbox">
              {gefilterd.map((s, i) => (
                <li
                  key={s.naam}
                  id={optieId(i)}
                  role="option"
                  aria-selected={i === gemarkeerd}
                >
                  <button
                    type="button"
                    className={
                      i === gemarkeerd
                        ? 'vs-autocomplete__optie vs-autocomplete__optie--actief'
                        : 'vs-autocomplete__optie'
                    }
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => kiesSuggestie(s.naam)}
                    onMouseEnter={() => setGemarkeerd(i)}
                  >
                    {s.naam}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="vs-field">
        <label className="vs-label" htmlFor={`${idPrefix}-aantal`}>
          Aantal
        </label>
        <input
          id={`${idPrefix}-aantal`}
          className="vs-input"
          type="number"
          min="1"
          step="any"
          inputMode="decimal"
          value={waarden.aantal}
          onChange={(e) => zet('aantal', e.target.value)}
        />
      </div>

      <div className="vs-field">
        <label className="vs-label" htmlFor={`${idPrefix}-eenheid`}>
          Eenheid
        </label>
        <input
          id={`${idPrefix}-eenheid`}
          className="vs-input"
          value={waarden.eenheid}
          onChange={(e) => zet('eenheid', e.target.value)}
          placeholder="liter, krat…"
          autoComplete="off"
        />
      </div>

      <button className="vs-btn vs-form__submit" type="submit" disabled={bezig}>
        {bezig ? 'Bezig…' : knopLabel}
      </button>

      {fout && (
        <div className="vs-field__error" style={{ gridColumn: '1 / -1' }} role="alert">
          {fout}
        </div>
      )}
    </form>
  );
}
